import { useMemo, type ReactElement } from 'react';
import { useSubmitHandler } from '@ff-it/form';
import { Box, LoadingContainer } from '@ff-it/ui';
import useSWR from 'swr';
import { toast } from 'react-toastify';
import type { ActivityType, ProductKind } from 'types';
import { fetcher } from 'services';
import type { OutletOrServiceCategory } from '../outletOrServiceCategory/types';
import type { OutletOrServiceCategoryProduct, ProductFormConfig } from './types';
import { ProductDrawerFrame, type ProductDrawerFrameContent } from './ProductDrawerFrame';
import type { ProductDrawerRequest } from './drawerState';
import { productKindSection } from './kindSections';
import { Form as ProductFields } from './Form';
import { ProductDrawerActions } from './ProductDrawerActions';
import { ProductFormShell } from './ProductFormShell';
import { productFormInitialValues, productFormValuesFromProduct, type ProductFormValues } from './formValues';

export type OutletProductCreateSeed = {
  kind?: ProductKind;
  initialValues?: ProductFormValues;
};

type OutletProductDrawerProps = {
  endpoint: string;
  outletOrServiceCategory: OutletOrServiceCategory;
  type: ActivityType;
  formConfig?: ProductFormConfig;
  request: ProductDrawerRequest<OutletProductCreateSeed> | null;
  onClose: () => void;
  onSaved: () => void;
  onCopy: (product: OutletOrServiceCategoryProduct) => void;
};

/**
 * Read-mostly entity context for catalogue actions rendered from a drawer is
 * provided by the shared `drawerEntityContext` helper.
 */

export function OutletProductDrawer({
  endpoint,
  outletOrServiceCategory,
  type,
  formConfig,
  request,
  onClose,
  onSaved,
  onCopy,
}: OutletProductDrawerProps): ReactElement {
  const productUrl = request?.mode === 'edit' ? `${endpoint}${request.productId}/` : null;
  const {
    data: product,
    isLoading,
    error,
    mutate: mutateProduct,
  } = useSWR<OutletOrServiceCategoryProduct>(productUrl ? { url: productUrl, method: 'GET' } : null, fetcher);

  const creating = request?.mode === 'create';
  const drawerKind = product?.kind ?? (request?.mode === 'create' ? request.seed?.kind : undefined);
  const initialValues = useMemo<ProductFormValues | null>(() => {
    if (creating) {
      return {
        ...productFormInitialValues(type),
        ...request.seed?.initialValues,
        kind: request.seed?.kind ?? request.seed?.initialValues?.kind ?? null,
        type,
      };
    }
    if (!product) return null;
    return productFormValuesFromProduct(product);
  }, [creating, product, request, type]);

  const submitUrl = product ? productUrl! : endpoint;
  const onSubmit = useSubmitHandler<ProductFormValues, OutletOrServiceCategoryProduct>(
    { url: submitUrl, method: product ? 'PUT' : 'POST' },
    {
      onSuccess: () => {
        toast.success(product ? 'Item has been updated.' : 'Item has been created.', {
          toastId: 'outlet-product-drawer-toast',
        });
        onSaved();
        onClose();
      },
    },
  );

  let content: ProductDrawerFrameContent | null = null;
  if (request) {
    content = {
      title: creating
        ? drawerKind
          ? productKindSection(drawerKind).createLabel
          : 'Create product'
        : `${product?.name || 'Generic product'}`,
      subtitle: `${type} · ${outletOrServiceCategory.name}`,
      body: error ? (
        <Box p="4" color="gray600">
          Unable to load product.
        </Box>
      ) : isLoading || !initialValues ? (
        <LoadingContainer loading testId="outlet-product-drawer-loading">
          {null}
        </LoadingContainer>
      ) : (
        <Box>
          <ProductFormShell
            initialValues={initialValues}
            onSubmit={onSubmit}
            resetKey={product?.id ?? 'create'}
            creating={creating}
            onClose={onClose}
            footerActions={
              product && productUrl ? (
                <ProductDrawerActions
                  product={product}
                  type={type}
                  endpoint={productUrl}
                  onClose={onClose}
                  onChanged={onSaved}
                  mutate={mutateProduct}
                  onCopy={() => onCopy(product)}
                />
              ) : undefined
            }
          >
            <ProductFields outletOrServiceCategory={outletOrServiceCategory} typeScope={type} formConfig={formConfig} />
          </ProductFormShell>
        </Box>
      ),
    };
  }

  return (
    <ProductDrawerFrame open={request !== null} onClose={onClose} content={content} testId="outlet-product-drawer" />
  );
}
